import styles from "./rich-text-renderer.module.less";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import PropTypes from "prop-types";
import { extract } from "@parksandresorts/core";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import RichProduct from "components/rich-text-elements/rich-product/rich-product";
import RichTextFeature from "components/rich-text-elements/rich-text-feature/rich-text-feature";

const renderInlineEntryHyperLink = (node, next) => {
  const contentType = extract(node, "data.target.__typename");
  switch (contentType) {
    case "ContentfulContentPage": {
      const slug = extract(node, "data.target.slug");
      return slug ? <LinkWrapper url={slug}>{next}</LinkWrapper> : next;
    }
    case "ContentfulLink": {
      const openInNewTab = extract(node, "data.target.openInNewTab");
      const target = openInNewTab ? "_blank" : undefined;

      const internalLinkSlug = extract(node, "data.target.internalLink.slug");
      const externalUrl = extract(node, "data.target.externalUrl");

      const url = externalUrl ? externalUrl : internalLinkSlug;
      return url ? (
        <LinkWrapper url={url} target={target}>
          {next}
        </LinkWrapper>
      ) : (
        next
      );
    }
    default: {
      return next;
    }
  }
};

const renderBlockEmbeddedEntry = (node) => {
  const blockProps = extract(node, "data.target");
  const type = blockProps.__typename;

  switch (type) {
    case "ContentfulProductBlock": {
      return <RichProduct {...blockProps} contentful_id={blockProps.contentful_id} />;
    }
    case "ContentfulRichTextFeature": {
      return <RichTextFeature {...blockProps} />;
    }
    default: {
      console.warn("WARNING: Unsupported block type in rich text renderer: ", type);
      return undefined;
    }
  }
};

const getOptions = () => {
  return {
    renderNode: {
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        return;
      },
      [INLINES.HYPERLINK]: (node, next) => {
        const uri = extract(node, "data.uri");
        return uri ? <LinkWrapper url={uri}>{next}</LinkWrapper> : { next };
      },
      [INLINES.ENTRY_HYPERLINK]: (node, next) => {
        return renderInlineEntryHyperLink(node, next);
      },
      [INLINES.ASSET_HYPERLINK]: (node, next) => {
        const url = extract(node, "data.target.file.url");
        return url ? <a href={url}>{next}</a> : { next };
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        return renderBlockEmbeddedEntry(node);
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const file = extract(node, "data.target.file");
        const image = extract(node, "data.target.file.contentType");
        const title = extract(node, "data.target.title");

        if (image?.includes("image")) {
          return <img className={styles.image} src={file.url} alt={title} />;
        }
      },
      [BLOCKS.HEADING_1]: (node, next) => <h1>{next}</h1>,
      [BLOCKS.HEADING_2]: (node, next) => <h2>{next}</h2>,
      [BLOCKS.HEADING_3]: (node, next) => <h3>{next}</h3>,
      [BLOCKS.HEADING_4]: (node, next) => <h4>{next}</h4>,
      [BLOCKS.HEADING_5]: (node, next) => <h5>{next}</h5>,
      [BLOCKS.HEADING_6]: (node, next) => <h6>{next}</h6>,
      [BLOCKS.PARAGRAPH]: (node, next) => <p className="paragraph">{next}</p>,
      [BLOCKS.UL_LIST]: (node, next) => <ul className="ul-list">{next}</ul>,
      [BLOCKS.LIST_ITEM]: (node, next) => <li className="ul-list-item">{next}</li>,
      [BLOCKS.QUOTE]: (node, next) => <blockquote className="blockquote">{next}</blockquote>,
      [BLOCKS.HR]: (node, next) => <hr className="hr" />,
    },
    renderMark: {
      [MARKS.BOLD]: (text) => <b className="bold">{text}</b>,
      [MARKS.ITALIC]: (text) => <i className="italic">{text}</i>,
      [MARKS.UNDERLINE]: (text) => <u className="underline">{text}</u>,
      [MARKS.CODE]: (text) => <code className="code">{text}</code>,
    },
    renderText: (text) => {
      return text.split("\n").reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
  };
};

const options = getOptions();

const RichTextRenderer = (props) => {
  if (!props.richTextData || !props.richTextData.raw) {
    return null;
  }

  return renderRichText(props.richTextData, options);
};

RichTextRenderer.propTypes = {
  richTextData: PropTypes.object,
};

export default RichTextRenderer;
